// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/customers/CustomerReport.tsx
// 
// تغییرات این نسخه:
// - 🆕 ترتیب مرتب‌سازی تراکنش‌ها: قدیمی‌ترین اول، جدیدترین آخر
//   در تاریخ یکسان، id کوچک‌تر اول می‌آید (آخرین فروش همیشه آخرین ردیف)
// 
// هیچ بخش دیگری دست نخورده است (همه فیچرهای قبلی حفظ شدند)
// ============================================================

import { useState, useCallback } from "react";
import { gregorianToJalaliStr, todayGregorian } from "../../utils/PersianDatePicker";
import PersianDatePicker from "../../utils/PersianDatePicker";
import { X, Printer, FileText, Loader } from "lucide-react";
import { journalAPI } from "../../services/api";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(Number(n) || 0));

interface Props {
  entity: any;
  entityType: "customer" | "vendor";
  onClose: () => void;
  T: any;
  dark: boolean;
}

export default function CustomerReport({ entity, entityType, onClose, T, dark }: Props) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [loading, setLoading]   = useState(false);
  const [txns, setTxns]         = useState<any[] | null>(null);

  const isCustomer = entityType === "customer";
  const mainColor  = isCustomer ? "#8b5cf6" : "#f59e0b";
  const entityName = [entity.first_name, entity.last_name, entity.company_name ? `(${entity.company_name})` : ""].filter(Boolean).join(" ");

  const loadReport = useCallback(async (from = dateFrom, to = dateTo) => {
    setLoading(true);

    // خواندن مستقیم از جورنال
    const [salesRes, paymentsRes, paymentsOutRes] = await Promise.all([
      journalAPI.listSales(),
      journalAPI.listPaymentsIn(),
      journalAPI.listPaymentsOut(),
    ]);

    const allSales       = (salesRes.success       ? salesRes.sales       || [] : []) as any[];
    const allPayments    = (paymentsRes.success     ? paymentsRes.payments || [] : []) as any[];
    const allPaymentsOut = (paymentsOutRes.success  ? paymentsOutRes.payments || [] : []) as any[];

    // فیلتر بر اساس مشتری
    const custSales       = allSales.filter((s: any)    => String(s.customer_id) === String(entity.id));
    const custPayments    = allPayments.filter((p: any) => String(p.customer_id) === String(entity.id));
    const custPaymentsOut = allPaymentsOut.filter((p: any) => String(p.customer_id) === String(entity.id));

    // فیلتر تاریخ
    const inRange = (date: string) => {
      if (from && date < from) return false;
      if (to   && date > to)   return false;
      return true;
    };

    // ساخت لیست تراکنش‌ها
    const rows: any[] = [];

    // ردیف اول: بیلانس اولیه
    const ob  = parseFloat(entity.opening_balance || 0);
    const obt = entity.opening_balance_type || "debit";
    rows.push({
      key:   "ob",
      type:  "opening_balance",
      id:    0, // 🆕 برای ترتیب
      date:  entity.created_at ? entity.created_at.substring(0, 10) : "",
      label: "🏦 بیلانس اولیه",
      debit:  obt === "debit"  ? ob : 0,
      credit: obt === "credit" ? ob : 0,
      details: [],
      currency: entity.currency,
      serial: "",
      truck_plate: "",
    });

    // فروش‌ها
    custSales.filter((s: any) => inRange(s.date)).forEach((s: any) => {
      rows.push({
        key:      `sale-${s.id}`,
        type:     "sale",
        id:       parseInt(s.id), // 🆕 برای ترتیب در تاریخ یکسان
        date:     s.date,
        label:    "📦 فروش",
        serial:   s.serial,
        truck_plate: s.truck_plate || "",
        debit:    parseFloat(s.net_amount || 0),
        credit:   0,
        currency: s.customer_currency || entity.currency,
        discount: parseFloat(s.discount || 0),
        notes:    s.notes || "",
        details:  (s.items || []).map((it: any) => ({
          product_name: it.product_name,
          quantity:     it.quantity,
          unit:         it.unit,
          unit_price:   it.unit_price,
          total_price:  it.total_price,
          warehouse:    it.warehouse_name,
        })),
      });
    });

    // دریافت پول
    custPayments.filter((p: any) => inRange(p.date)).forEach((p: any) => {
      rows.push({
        key:              `pay-${p.id}`,
        type:             "payment_in",
        id:               parseInt(p.id), // 🆕
        date:             p.date,
        label:            "💵 دریافت پول",
        serial:           p.serial,
        truck_plate:      p.truck_plate || "",
        debit:            0,
        credit:           parseFloat(p.customer_amount || 0),
        currency:         p.customer_currency || entity.currency,
        receipt_amount:   parseFloat(p.receipt_amount || 0),
        receipt_currency: p.receipt_currency,
        exchange_rate:    parseFloat(p.exchange_rate || 1),
        account_name:     p.account_name,
        notes:            p.notes || "",
        details:          [],
      });
    });

    // پرداخت به مشتری (بازپرداخت)
    custPaymentsOut.filter((p: any) => inRange(p.date)).forEach((p: any) => {
      rows.push({
        key:            `payout-${p.id}`,
        type:           "payment_out",
        id:             parseInt(p.id), // 🆕
        date:           p.date,
        label:          "💸 پرداخت به مشتری",
        serial:         p.serial,
        truck_plate:    p.truck_plate || "",
        debit:          0,
        credit:         0,
        refund:         parseFloat(p.vendor_amount || 0),
        currency:       p.vendor_currency || entity.currency,
        payment_amount: parseFloat(p.payment_amount || 0),
        payment_currency: p.payment_currency,
        account_name:   p.account_name,
        notes:          p.notes || "",
        details:        [],
      });
    });

    // 🆕 مرتب‌سازی: opening_balance اول، بقیه به ترتیب date ASC و id ASC
    //   در تاریخ یکسان، id کوچک‌تر (قدیمی‌تر) اول می‌آید
    //   آخرین فروش/تراکنش همیشه آخرین ردیف است
    rows.sort((a, b) => {
      if (a.type === "opening_balance") return -1;
      if (b.type === "opening_balance") return 1;
      const dc = a.date.localeCompare(b.date);
      if (dc !== 0) return dc;
      return (a.id || 0) - (b.id || 0);
    });

    // محاسبه running balance
    let runBal = obt === "debit" ? ob : -ob;
    const result = rows.map(tx => {
      if (tx.type === "opening_balance") {
        runBal = tx.debit > 0 ? tx.debit : -tx.credit;
      } else if (tx.type === "payment_out") {
        runBal += tx.refund; // پرداخت به مشتری — بیلانس مثبت‌تر (قرضدارتر)
      } else {
        runBal += tx.debit - tx.credit;
      }
      return { ...tx, balance: runBal };
    });

    setTxns(result);
    setLoading(false);
  }, [entity, dateFrom, dateTo]);

  const setQuick = (type: string) => {
    const today = new Date();
    const pad   = (n: number) => String(n).padStart(2, "0");
    const fmtD  = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    let from = "", to = todayGregorian();
    switch(type) {
      case "today":  from = to = todayGregorian(); break;
      case "week":   { const d = new Date(today); d.setDate(today.getDate()-6); from = fmtD(d); break; }
      case "month":  { from = fmtD(new Date(today.getFullYear(), today.getMonth(), 1)); break; }
      case "3month": { const d = new Date(today); d.setMonth(today.getMonth()-3); from = fmtD(d); break; }
      case "year":   { from = fmtD(new Date(today.getFullYear(), 0, 1)); break; }
      case "all":    from = ""; to = ""; break;
    }
    setDateFrom(from); setDateTo(to);
    loadReport(from, to);
  };

  const openingRow = txns?.find(t => t.type === "opening_balance");
  const openingBal = openingRow ? (openingRow.debit - openingRow.credit) : 0;
  const otherTxns  = txns?.filter(t => t.type !== "opening_balance") || [];
  const totalSales   = otherTxns.filter(t => t.type === "sale").reduce((s, t) => s + t.debit, 0);
  const totalPay     = otherTxns.filter(t => t.type === "payment_in").reduce((s, t) => s + t.credit, 0);
  const totalPayOut  = otherTxns.filter(t => t.type === "payment_out").reduce((s, t) => s + t.refund, 0);
  const finalBal   = txns && txns.length > 0 ? txns[txns.length - 1].balance : 0;

  const handlePrint = () => {
    if (!txns || txns.length === 0) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(buildPrint(entity, entityName, txns, openingBal, totalSales, totalPay, finalBal, dateFrom, dateTo, mainColor));
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  const bg  = dark ? "#1a1625" : "#fff";
  const bdr = dark ? "rgba(255,255,255,0.1)" : "#e2e8f0";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: bg, borderRadius: 20, padding: 24, width: "100%", maxWidth: 1100, maxHeight: "93vh", overflow: "auto", boxShadow: "0 25px 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* هدر */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg,${mainColor},${mainColor}cc)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
              {isCustomer ? "🤝" : "🏪"}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{entityName}</div>
              <div style={{ fontSize: 11, color: T.subtext }}>کد: <strong style={{ color: mainColor }}>{entity.code}</strong> | ارز: <strong style={{ color: T.text }}>{entity.currency}</strong></div>
              <div style={{ fontSize: 11, color: T.subtext }}>موجودی فعلی: <strong style={{ color: entity.current_balance >= 0 ? "#ef4444" : "#10b981" }}>{fmt(entity.current_balance)} {entity.currency}</strong></div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={20} /></button>
        </div>

        {/* فیلتر */}
        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", borderRadius: 12, padding: 14, border: `1px solid ${bdr}` }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.subtext }}>🗓 دوره:</span>
            {[{k:"all",l:"همه"},{k:"today",l:"امروز"},{k:"week",l:"۷ روز"},{k:"month",l:"این ماه"},{k:"3month",l:"۳ ماه"},{k:"year",l:"امسال"}].map(q => (
              <button key={q.k} onClick={() => setQuick(q.k)} style={{ padding: "4px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${mainColor}50`, background: "transparent", color: mainColor, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>{q.l}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div><div style={{ fontSize: 10, color: T.subtext, marginBottom: 3 }}>از تاریخ</div><PersianDatePicker value={dateFrom} onChange={setDateFrom} /></div>
            <div><div style={{ fontSize: 10, color: T.subtext, marginBottom: 3 }}>تا تاریخ</div><PersianDatePicker value={dateTo} onChange={setDateTo} /></div>
            <button onClick={() => loadReport()} disabled={loading} style={{ padding: "9px 20px", borderRadius: 9, border: "none", cursor: loading ? "not-allowed" : "pointer", background: `linear-gradient(135deg,${mainColor},${mainColor}cc)`, color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", display: "flex", alignItems: "center", gap: 6, opacity: loading ? 0.7 : 1 }}>
              {loading ? <Loader size={14} /> : "🔍"} نمایش گزارش
            </button>
            {txns && txns.length > 0 && (
              <button onClick={handlePrint} style={{ padding: "9px 20px", borderRadius: 9, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
                <Printer size={14} /> پرینت
              </button>
            )}
          </div>
        </div>

        {/* خلاصه */}
        {txns && txns.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 8 }}>
            {[
              { label: "بیلانس اولیه", val: fmt(Math.abs(openingBal)), color: "#64748b", sign: openingBal >= 0 ? "+" : "-" },
              { label: "جمع فروش‌ها",  val: fmt(totalSales),           color: "#ef4444", sign: "+" },
              { label: "جمع دریافتی",  val: fmt(totalPay),             color: "#10b981", sign: "-" },
              { label: "بیلانس نهایی", val: fmt(Math.abs(finalBal)),   color: finalBal >= 0 ? "#ef4444" : "#10b981", sign: finalBal >= 0 ? "+" : "-" },
            ].map(item => (
              <div key={item.label} style={{ background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1px solid ${bdr}`, borderRadius: 10, padding: "10px 12px", borderTop: `3px solid ${item.color}` }}>
                <div style={{ fontSize: 10, color: T.subtext, marginBottom: 3 }}>{item.label}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.sign}{item.val}</div>
                <div style={{ fontSize: 9, color: T.subtext }}>{entity.currency}</div>
              </div>
            ))}
          </div>
        )}

        {/* جدول */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 50, color: T.subtext }}>در حال بارگذاری...</div>
        ) : txns === null ? (
          <div style={{ textAlign: "center", padding: 50, color: T.subtext, fontSize: 13 }}>برای مشاهده گزارش یک دوره انتخاب کنید یا روی "همه" کلیک کنید.</div>
        ) : txns.length === 0 ? (
          <div style={{ textAlign: "center", padding: 50, color: T.subtext }}>هیچ معامله‌ای یافت نشد</div>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${bdr}` }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: dark ? "#1e293b" : "#334155" }}>
                  {["تاریخ","شماره سند","🚛 شماره موتر","نوع","جزئیات","ارز","فروش (قرضدار)","دریافتی (طلبکار)","بیلانس"].map(h => (
                    <th key={h} style={{ padding: "9px 10px", textAlign: "right", fontWeight: 700, color: "#f1f5f9", fontSize: 11, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txns.map((tx: any, i: number) => {
                  const isOb     = tx.type === "opening_balance";
                  const isSale   = tx.type === "sale";
                  const isPay    = tx.type === "payment_in";
                  const isPayOut = tx.type === "payment_out";
                  const bal = tx.balance;
                  return (
                    <tr key={tx.key} style={{ background: isOb ? (dark ? "rgba(16,185,129,0.1)" : "#f0fdf4") : isPayOut ? (dark ? "rgba(249,115,22,0.06)" : "#fff7ed") : i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc"), borderBottom: isOb ? `2px solid #86efac` : `1px solid ${bdr}` }}>
                      <td style={{ padding: "8px 10px", color: T.text, whiteSpace: "nowrap" }}>{gregorianToJalaliStr(tx.date)}</td>
                      <td style={{ padding: "8px 10px", color: mainColor, fontWeight: 700, fontSize: 11 }}>{tx.serial || "—"}</td>
                      <td style={{ padding: "8px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
                        {tx.truck_plate ? (
                          <span style={{ background: dark ? "rgba(59,130,246,0.18)" : "#dbeafe", color: dark ? "#93c5fd" : "#1d4ed8", padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 800, letterSpacing: 0.3, fontFamily: "monospace" }}>
                            {tx.truck_plate}
                          </span>
                        ) : (
                          <span style={{ color: T.subtext, fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{ background: isOb ? "#d1fae5" : isSale ? "#ede9fe" : isPayOut ? "#fed7aa" : "#dbeafe", color: isOb ? "#059669" : isSale ? "#7c3aed" : isPayOut ? "#c2410c" : "#1d4ed8", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {tx.label}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px", maxWidth: 280 }}>
                        {isOb && <span style={{ color: T.subtext, fontSize: 11 }}>بیلانس اولیه ثبت شده</span>}
                        {isSale && tx.details.map((it: any, j: number) => (
                          <div key={j} style={{ fontSize: 11, marginBottom: 2, background: dark ? "rgba(255,255,255,0.04)" : "#f5f3ff", padding: "2px 6px", borderRadius: 5 }}>
                            📦 {it.product_name} | {it.quantity} {it.unit} × {fmt(it.unit_price)} = <strong>{fmt(it.total_price)}</strong>
                            {it.warehouse ? <span style={{ color: "#10b981" }}> ({it.warehouse})</span> : null}
                          </div>
                        ))}
                        {isSale && tx.notes && <div style={{ fontSize: 10, color: T.subtext, marginTop: 2 }}>📝 {tx.notes}</div>}
                        {isPay && (
                          <div style={{ fontSize: 11 }}>
                            <div>💱 {fmt(tx.receipt_amount)} {tx.receipt_currency} {tx.exchange_rate !== 1 ? `× ${tx.exchange_rate}` : ""}</div>
                            <div style={{ color: "#10b981" }}>≈ {fmt(tx.credit)} {tx.currency}</div>
                            {tx.account_name && <div style={{ color: "#3b82f6" }}>🏦 {tx.account_name}</div>}
                            {tx.notes && <div style={{ color: T.subtext }}>📝 {tx.notes}</div>}
                          </div>
                        )}
                        {isPayOut && (
                          <div style={{ fontSize: 11 }}>
                            <div>💸 {fmt(tx.payment_amount)} {tx.payment_currency}</div>
                            <div style={{ color: "#f97316" }}>≈ {fmt(tx.refund)} {tx.currency}</div>
                            {tx.account_name && <div style={{ color: "#3b82f6" }}>🏦 {tx.account_name}</div>}
                            {tx.notes && <div style={{ color: T.subtext }}>📝 {tx.notes}</div>}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center", color: T.text }}>{tx.currency}</td>
                      <td style={{ padding: "8px 10px", fontWeight: tx.debit > 0 ? 800 : 400, color: tx.debit > 0 ? "#ef4444" : T.subtext, fontSize: 13 }}>
                        {tx.debit > 0 ? fmt(tx.debit) : "—"}
                      </td>
                      <td style={{ padding: "8px 10px", fontWeight: (tx.credit > 0 || tx.refund > 0) ? 800 : 400, color: (tx.credit > 0 || tx.refund > 0) ? "#10b981" : T.subtext, fontSize: 13 }}>
                        {tx.credit > 0 ? fmt(tx.credit) : tx.refund > 0 ? fmt(tx.refund) : "—"}
                      </td>
                      <td style={{ padding: "8px 10px", fontWeight: 800, fontSize: 13, color: bal >= 0 ? "#ef4444" : "#10b981", whiteSpace: "nowrap" }}>
                        {bal >= 0 ? "+" : ""}{fmt(bal)} <span style={{ fontSize: 10 }}>{entity.currency}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== پرینت =====
function buildPrint(entity: any, entityName: string, txns: any[], openingBal: number, totalSales: number, totalPay: number, finalBal: number, dateFrom: string, dateTo: string, mainColor: string): string {
  const fmt2 = (n: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));
  const period = dateFrom || dateTo ? `${dateFrom ? gregorianToJalaliStr(dateFrom) : "ابتدا"} تا ${dateTo ? gregorianToJalaliStr(dateTo) : "انتها"}` : "تمام دوره";

  const rows = txns.map((tx: any, i: number) => {
    const isOb   = tx.type === "opening_balance";
    const isSale = tx.type === "sale";
    const isPay  = tx.type === "payment_in";
    const isPayOut = tx.type === "payment_out";
    const bal    = tx.balance;

    let detailHTML = "";
    if (isOb) detailHTML = '<span style="color:#64748b">بیلانس اولیه ثبت شده</span>';
    if (isSale) {
      detailHTML = tx.details.map((it: any) =>
        `<div style="background:#f5f3ff;padding:2px 5px;border-radius:3px;margin-bottom:1px;font-size:9px;line-height:1.4">📦 ${it.product_name} | ${it.quantity} ${it.unit} × ${fmt2(it.unit_price)} = <b>${fmt2(it.total_price)}</b>${it.warehouse ? ` (${it.warehouse})` : ""}</div>`
      ).join("") + (tx.notes ? `<div style="color:#64748b;font-size:9px;margin-top:1px">📝 ${tx.notes}</div>` : "");
    }
    if (isPay) {
      detailHTML = `<div style="font-size:9px;line-height:1.5">
        <div>💱 ${fmt2(tx.receipt_amount)} ${tx.receipt_currency}${tx.exchange_rate !== 1 ? ` × ${tx.exchange_rate}` : ""}</div>
        <div style="color:#059669">≈ ${fmt2(tx.credit)} ${tx.currency}</div>
        ${tx.account_name ? `<div style="color:#3b82f6">🏦 ${tx.account_name}</div>` : ""}
        ${tx.notes ? `<div style="color:#64748b">📝 ${tx.notes}</div>` : ""}
      </div>`;
    }
    if (isPayOut) {
      detailHTML = `<div style="font-size:9px;line-height:1.5">
        <div>💸 ${fmt2(tx.payment_amount)} ${tx.payment_currency}</div>
        <div style="color:#c2410c">≈ ${fmt2(tx.refund)} ${tx.currency}</div>
        ${tx.account_name ? `<div style="color:#3b82f6">🏦 ${tx.account_name}</div>` : ""}
        ${tx.notes ? `<div style="color:#64748b">📝 ${tx.notes}</div>` : ""}
      </div>`;
    }

    const plateCell = tx.truck_plate
      ? `<span class="plate">${tx.truck_plate}</span>`
      : `<span style="color:#cbd5e1">—</span>`;

    const rowLabelBg = isOb ? "#d1fae5" : isSale ? "#ede9fe" : isPayOut ? "#fed7aa" : "#dbeafe";
    const rowLabelCl = isOb ? "#059669" : isSale ? "#7c3aed" : isPayOut ? "#c2410c" : "#1d4ed8";

    const creditVal = tx.credit > 0 ? tx.credit : (tx.refund > 0 ? tx.refund : 0);

    return `<tr class="${isOb ? "ob" : i % 2 === 0 ? "even" : "odd"}">
      <td class="c-date">${gregorianToJalaliStr(tx.date)}</td>
      <td class="c-serial">${tx.serial || "—"}</td>
      <td class="c-plate">${plateCell}</td>
      <td class="c-type"><span style="background:${rowLabelBg};color:${rowLabelCl};padding:2px 6px;border-radius:4px;font-size:9px;font-weight:700;white-space:nowrap">${tx.label}</span></td>
      <td class="c-details">${detailHTML}</td>
      <td class="c-curr">${tx.currency}</td>
      <td class="c-amt" style="font-weight:${tx.debit>0?"800":"400"};color:${tx.debit>0?"#dc2626":"#94a3b8"}">${tx.debit>0?fmt2(tx.debit):"—"}</td>
      <td class="c-amt" style="font-weight:${creditVal>0?"800":"400"};color:${creditVal>0?"#059669":"#94a3b8"}">${creditVal>0?fmt2(creditVal):"—"}</td>
      <td class="c-amt" style="font-weight:800;color:${bal>=0?"#dc2626":"#059669"}">${bal>=0?"+":""}${fmt2(bal)}</td>
    </tr>`;
  }).join("");

  return `<!DOCTYPE html><html dir="rtl" lang="fa"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>گزارش — ${entityName}</title>
<link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;600;700;800&display=swap" rel="stylesheet">
<style>
*{box-sizing:border-box;margin:0;padding:0}
@page{size:A4 landscape;margin:8mm 10mm}
html,body{font-family:'Vazirmatn',Tahoma,sans-serif;direction:rtl;color:#1e293b;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-size:10.5px;padding:0}
.wrap{max-width:100%;margin:0 auto}
.header{display:flex;justify-content:space-between;align-items:center;padding-bottom:10px;border-bottom:3px solid ${mainColor};margin-bottom:10px;gap:10px;flex-wrap:wrap}
.logo{width:38px;height:38px;background:linear-gradient(135deg,${mainColor},${mainColor}99);border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:19px;flex-shrink:0}
.title{font-size:16px;font-weight:800;color:#0f172a}
.sub{font-size:10.5px;color:#64748b;margin-top:2px}
.meta{text-align:left;font-size:9.5px;color:#64748b;line-height:1.7}
.meta strong{color:#1e1b2e}
.summary{display:flex;gap:6px;margin-bottom:10px;flex-wrap:wrap}
.sbox{flex:1;min-width:140px;border-radius:7px;padding:7px 10px}
.sbox.ob{background:#f8fafc;border:1px solid #e2e8f0}
.sbox.dr{background:#fff1f2;border:1px solid #fca5a5}
.sbox.cr{background:#f0fdf4;border:1px solid #86efac}
.sbox.fn{background:linear-gradient(135deg,${mainColor}10,${mainColor}05);border:1.5px solid ${mainColor}50}
.slabel{font-size:9px;font-weight:700;color:#64748b;margin-bottom:2px}
.sval{font-size:12px;font-weight:800}
.sec-title{font-size:11px;font-weight:800;color:#374151;margin-bottom:5px;padding-right:7px;border-right:3px solid ${mainColor}}

table{width:100%;border-collapse:collapse;font-size:9.5px;table-layout:fixed}
thead tr{background:linear-gradient(135deg,#1e293b,#334155)}
th{padding:6px 4px;text-align:right;font-weight:700;color:#f1f5f9;font-size:9.5px;white-space:nowrap;border-left:1px solid rgba(255,255,255,0.08)}
th:first-child{border-left:none}
td{padding:5px 4px;border-bottom:1px solid #f1f5f9;vertical-align:top;word-wrap:break-word;overflow-wrap:break-word}
.ob{background:#f0fdf4;border-bottom:2px solid #86efac!important}
.even{background:#fff}.odd{background:#f8fafc}
tr:last-child td{border-bottom:2px solid #e2e8f0}

/* عرض ستون‌ها — مجموع باید 100٪ بشه */
.c-date{width:7%;white-space:nowrap;font-size:9.5px}
.c-serial{width:8%;color:${mainColor};font-weight:700;font-size:9.5px;white-space:nowrap}
.c-plate{width:8%;text-align:center;white-space:nowrap}
.c-type{width:9%;white-space:nowrap}
.c-details{width:36%}
.c-curr{width:5%;text-align:center}
.c-amt{width:9%;text-align:left;white-space:nowrap;font-size:10px}

.plate{display:inline-block;background:#dbeafe;color:#1d4ed8;padding:2px 7px;border-radius:5px;font-size:10px;font-weight:800;letter-spacing:0.4px;font-family:'Vazirmatn',monospace;border:1px solid #93c5fd}

.footer{margin-top:12px;padding-top:8px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8;flex-wrap:wrap;gap:10px}
.sig{border-top:1px dashed #cbd5e1;padding-top:4px;min-width:140px;text-align:center;font-size:9.5px;color:#64748b}

@media print{
  thead{display:table-header-group}
  tr{page-break-inside:avoid}
  .header{margin-bottom:8px}
  .summary{margin-bottom:8px}
}

/* موبایل — حالت مشاهده */
@media screen and (max-width:768px){
  body{font-size:11px;padding:8px}
  .header{flex-direction:column;align-items:flex-start}
  .meta{text-align:right}
  table{font-size:10px}
  .c-date,.c-serial,.c-plate,.c-type,.c-details,.c-curr,.c-amt{width:auto}
  table{table-layout:auto;min-width:900px}
  .wrap{overflow-x:auto}
}
</style></head><body>
<div class="wrap">

<div class="header">
  <div style="display:flex;align-items:center;gap:10px">
    <div class="logo">🤝</div>
    <div><div class="title">${entityName}</div><div class="sub">گزارش مشتری — ${period}</div></div>
  </div>
  <div class="meta">
    <div>کد: <strong>${entity.code}</strong></div>
    <div>ارز: <strong>${entity.currency}</strong></div>
    <div>موجودی فعلی: <strong style="color:${finalBal>=0?"#dc2626":"#059669"}">${finalBal>=0?"+":""}${fmt2(finalBal)} ${entity.currency}</strong></div>
    <div>تاریخ چاپ: <strong>${new Date().toLocaleDateString("fa-IR")}</strong></div>
  </div>
</div>

<div class="summary">
  <div class="sbox ob"><div class="slabel">بیلانس اولیه</div><div class="sval" style="color:#64748b">${openingBal>=0?"+":"-"}${fmt2(openingBal)} ${entity.currency}</div></div>
  <div class="sbox dr"><div class="slabel">▲ جمع فروش‌ها</div><div class="sval" style="color:#dc2626">+${fmt2(totalSales)} ${entity.currency}</div></div>
  <div class="sbox cr"><div class="slabel">▼ جمع دریافتی</div><div class="sval" style="color:#059669">-${fmt2(totalPay)} ${entity.currency}</div></div>
  <div class="sbox fn"><div class="slabel">بیلانس نهایی</div><div class="sval" style="color:${finalBal>=0?"#dc2626":"#059669"}">${finalBal>=0?"+":""}${fmt2(finalBal)} ${entity.currency}</div></div>
</div>

<div class="sec-title">جزئیات کامل معاملات (${txns.length} رکورد)</div>
<table>
  <thead><tr>
    <th class="c-date">تاریخ</th>
    <th class="c-serial">شماره سند</th>
    <th class="c-plate">🚛 شماره موتر</th>
    <th class="c-type">نوع</th>
    <th class="c-details">جزئیات</th>
    <th class="c-curr">ارز</th>
    <th class="c-amt">فروش (قرضدار)</th>
    <th class="c-amt">دریافتی (طلبکار)</th>
    <th class="c-amt">بیلانس</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>

<div class="footer">
  <span>سیستم مالی — گزارش مشتری: ${entityName} | ${period}</span>
  <div class="sig">امضا و مهر مسئول مالی</div>
</div>

</div>
</body></html>`;
}